/** Mail primitives exchanged over the BFF. */
/** Fraction of quota consumed, clamped to 0–1. Returns 0 for an unlimited box. */
export function quotaRatio(quota) {
    if (quota.limitBytes <= 0) {
        return 0;
    }
    return Math.min(1, Math.max(0, quota.usedBytes / quota.limitBytes));
}
/**
 * How full a mailbox is, as the warnings put it: `near` from 80%, `full` from
 * 95%, when the server has already written to say so (quota_warning).
 */
export function quotaLevel(quota) {
    const ratio = quotaRatio(quota);
    return ratio >= 0.95 ? 'full' : ratio >= 0.8 ? 'near' : '';
}
/** How the path of an archive's folder starts (ADR-0040). */
export const ARCHIVE_PREFIX = 'local:';
/** Whether a folder path is an archive's, on this computer. */
export function isArchivePath(path) {
    return path.startsWith(ARCHIVE_PREFIX);
}
/** An archive's folder path within the archive: `Inbox/2019`. */
export function archiveInner(path) {
    if (!isArchivePath(path))
        return path;
    const rest = path.slice(ARCHIVE_PREFIX.length);
    const cut = rest.indexOf(FOLDER_SEPARATOR);
    return cut < 0 ? '' : rest.slice(cut + 1);
}
/** The path the screens use for the top of archive `id`. */
export function archiveRoot(id) {
    return `${ARCHIVE_PREFIX}${id}`;
}
/** The level separator in folder paths (Dovecot's `separator = /`). */
export const FOLDER_SEPARATOR = '/';
export const FOLDER_NAME_MAX = 100;
/** Kept by the app for scheduled and snoozed mail (ADR-0051): never a parent. */
const APP_FOLDERS = new Set(['Scheduled', 'Snoozed']);
/** Top-level names the server keeps: the Inbox, and other people's mail. */
const RESERVED_TOP = new Set(['inbox', 'shared']);
/**
 * Why `name` cannot name a folder, or null when it can. The same rules hold
 * in webmail's server and the desktop engine; this copy lets the form say so
 * before asking.
 */
export function folderNameProblem(name) {
    const n = name.trim();
    if (!n)
        return 'Give the folder a name.';
    if (n.length > FOLDER_NAME_MAX)
        return `Keep the name to ${FOLDER_NAME_MAX} characters.`;
    if ([...n].some((c) => c.charCodeAt(0) < 0x20 || c.charCodeAt(0) === 0x7f)) {
        return 'The name has a character a folder cannot have.';
    }
    if (/[/\\%*]/.test(n))
        return 'A folder name cannot contain / \\ % or *.';
    if (n.startsWith('.'))
        return 'A folder name cannot start with a dot.';
    return null;
}
/** Kept by name for the app's own work: never renamed or deleted. */
const KEPT_FOLDERS = new Set([...APP_FOLDERS, 'Templates']);
/**
 * Whether the person may rename or delete `folder`: one of their own that
 * they made — not the Inbox, Sent, Trash and the rest, not the app's, not
 * someone else's.
 */
export function canChangeFolder(folder) {
    return (!folder.shared &&
        !folder.specialUse &&
        folder.path.toUpperCase() !== 'INBOX' &&
        !KEPT_FOLDERS.has(folder.path));
}
/**
 * The path a folder gets when renamed, in the same place, or why it cannot
 * be. Changing only the case of its name is allowed.
 */
export function renamedFolderPath(path, name, folders) {
    const folder = folders.find((f) => f.path === path);
    if (!folder || !canChangeFolder(folder)) {
        return { code: 'invalid', problem: 'This folder cannot be renamed.' };
    }
    const cut = path.lastIndexOf(FOLDER_SEPARATOR);
    const parent = cut < 0 ? undefined : path.slice(0, cut);
    const made = newFolderPath(name, parent, folders.filter((f) => f.path !== path));
    if ('path' in made && made.path === path) {
        return { code: 'invalid', problem: 'That is its name already.' };
    }
    return made;
}
/**
 * The path a folder gets when moved into `parent` (undefined or empty: the
 * top level), keeping its name, or why it cannot go there.
 */
export function movedFolderPath(path, parent, folders) {
    const folder = folders.find((f) => f.path === path);
    if (!folder || !canChangeFolder(folder)) {
        return { code: 'invalid', problem: 'This folder cannot be moved.' };
    }
    const target = parent || undefined;
    if (target && within(target, path)) {
        return { code: 'invalid', problem: 'A folder cannot go inside itself.' };
    }
    const cut = path.lastIndexOf(FOLDER_SEPARATOR);
    if ((cut < 0 ? undefined : path.slice(0, cut)) === target) {
        return { code: 'invalid', problem: 'It is there already.' };
    }
    return newFolderPath(cut < 0 ? path : path.slice(cut + 1), target, folders.filter((f) => f.path !== path));
}
/** Where `folder` can be moved: the folders it may go into (not itself, not its own). */
export function folderMoveTargets(folder, folders) {
    // An archive's folders stay where they are, and no folder moves into one:
    // its mail is moved instead.
    if (folder.archive)
        return [];
    const cut = folder.path.lastIndexOf(FOLDER_SEPARATOR);
    const current = cut < 0 ? undefined : folder.path.slice(0, cut);
    return folders.filter((f) => !f.archive && canHoldFolders(f) && !within(f.path, folder.path) && f.path !== current);
}
/** Whether `folder` is `path` or inside it. */
function within(folder, path) {
    return folder === path || folder.startsWith(path + FOLDER_SEPARATOR);
}
/** The names of the rules that file into `path` or a folder inside it. */
export function rulesFilingInto(rules, path) {
    return rules
        .filter((r) => r.actions.some((a) => (a.type === 'move' || a.type === 'copy') && within(a.folder, path)))
        .map((r) => r.name);
}
/** The rules with every folder under `from` now under `to`, and how many changed. */
export function renameInRules(rules, from, to) {
    let changed = 0;
    const next = rules.map((r) => {
        let touched = false;
        const actions = r.actions.map((a) => {
            if ((a.type === 'move' || a.type === 'copy') && within(a.folder, from)) {
                touched = true;
                return { ...a, folder: to + a.folder.slice(from.length) };
            }
            return a;
        });
        if (touched)
            changed += 1;
        return touched ? { ...r, actions } : r;
    });
    return { rules: next, changed };
}
/** Whether a new folder can go inside `folder`: the person's own, not the app's. */
export function canHoldFolders(folder) {
    return !folder.shared && !APP_FOLDERS.has(folder.path);
}
/**
 * The path a new folder gets, or why it cannot be made: an invalid name or
 * parent (`invalid`), or a folder of that name already there (`exists`).
 */
export function newFolderPath(name, parent, folders) {
    const problem = folderNameProblem(name);
    if (problem)
        return { code: 'invalid', problem };
    const leaf = name.trim();
    // The top of an archive on this computer holds folders, and is not one.
    const archiveTop = parent !== undefined &&
        isArchivePath(parent) &&
        !parent.slice(ARCHIVE_PREFIX.length).includes(FOLDER_SEPARATOR);
    if (parent && !archiveTop) {
        const holder = folders.find((f) => f.path === parent);
        if (!holder || !canHoldFolders(holder)) {
            return { code: 'invalid', problem: 'A new folder cannot go there.' };
        }
    }
    else if (RESERVED_TOP.has(leaf.toLowerCase())) {
        return { code: 'invalid', problem: `${leaf} is a name the mail server keeps for itself.` };
    }
    const path = parent ? `${parent}${FOLDER_SEPARATOR}${leaf}` : leaf;
    if (folders.some((f) => !f.shared && f.path.toLowerCase() === path.toLowerCase())) {
        return { code: 'exists', problem: `There is already a folder called ${leaf} there.` };
    }
    return { path };
}
/** How deep a folder of the person's own sits: 0 at the top. */
export function folderDepth(folder) {
    if (folder.shared)
        return 0;
    const path = folder.archive ? archiveInner(folder.path) : folder.path;
    return path.split(FOLDER_SEPARATOR).length - 1;
}
/**
 * A folder's name with the folders it is in, for a list without indents:
 * "Projects › Acme". Names come from the list, so an encoded path never shows.
 */
export function folderLabel(folder, folders) {
    if (folder.archive) {
        const root = archiveRoot(folder.archive.id);
        const parts = archiveInner(folder.path).split(FOLDER_SEPARATOR);
        const names = parts.map((part, i) => {
            const path = `${root}${FOLDER_SEPARATOR}${parts.slice(0, i + 1).join(FOLDER_SEPARATOR)}`;
            return folders.find((f) => f.path === path)?.name ?? part;
        });
        return [folder.archive.name, ...names].join(' › ');
    }
    if (folder.shared || !folder.path.includes(FOLDER_SEPARATOR))
        return folder.name;
    const parts = folder.path.split(FOLDER_SEPARATOR);
    const names = parts.map((part, i) => {
        const path = parts.slice(0, i + 1).join(FOLDER_SEPARATOR);
        return folders.find((f) => f.path === path)?.name ?? (path === 'INBOX' ? 'Inbox' : part);
    });
    return names.join(' › ');
}
//# sourceMappingURL=mail.js.map